import React, { useState, useEffect, useRef } from 'react';
import { CountryDropdown } from 'react-country-region-selector';
import { Calendar, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { useKYCStatus, useSubmitKYC } from '@/hooks/useSettings';
import DatePicker from '@/components/common/DatePicker/DatePicker';
import styles from './VerificationForm.module.css';

/**
 * VerificationForm Component
 * Full-page KYC verification form with 3 steps
 */
const VerificationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [documentType, setDocumentType] = useState('idCard');
  const [errors, setErrors] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);

  // React Query hooks
  const { data: kycStatusData } = useKYCStatus();
  const kycStatus = kycStatusData?.status || null;
  const submitKYCMutation = useSubmitKYC();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: null,
    address: '',
    city: '',
    country: '',
  });

  const [previewImages, setPreviewImages] = useState({
    idCardFront: null,
    idCardBack: null,
    passport: null,
  });

  const fileInputRefs = {
    idCardFront: useRef(null),
    idCardBack: useRef(null),
    passport: useRef(null),
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        return !value || value.trim() === ''
          ? 'This field is required'
          : !/^[a-zA-Z\s]+$/.test(value)
          ? 'Only letters are allowed'
          : '';
      case 'gender':
      case 'country':
        return !value ? 'Please select an option' : '';
      case 'dateOfBirth':
        if (!value) return 'Date of birth is required';
        const date = value instanceof Date ? value : new Date(value);
        const age = new Date().getFullYear() - date.getFullYear();
        return age < 18 ? 'Must be at least 18 years old' : '';
      case 'address':
      case 'city':
        return !value || value.trim() === '' ? 'This field is required' : '';
      default:
        return !value ? 'This field is required' : '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: date,
    }));

    const error = validateField('dateOfBirth', date);
    setErrors((prev) => ({
      ...prev,
      dateOfBirth: error,
    }));
  };

  const handleDocumentTypeChange = (type) => {
    setDocumentType(type);
    setErrors({});
    if (type === 'idCard') {
      setPreviewImages((prev) => ({
        ...prev,
        passport: null,
      }));
    } else {
      setPreviewImages((prev) => ({
        ...prev,
        idCardFront: null,
        idCardBack: null,
      }));
    }
  };

  const handleFileSelect = (type) => {
    fileInputRefs[type].current?.click();
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [type]: 'File size should not exceed 1MB',
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages((prev) => ({
          ...prev,
          [type]: e.target.result,
        }));
        setFormData((prev) => ({
          ...prev,
          [type]: file,
        }));
        setErrors((prev) => ({
          ...prev,
          [type]: '',
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    const fields = ['firstName', 'lastName', 'gender', 'dateOfBirth', 'address', 'city', 'country'];

    fields.forEach((field) => {
      const value = formData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (documentType === 'idCard') {
      if (!formData.idCardFront) {
        newErrors.idCardFront = 'ID Card Front is required';
      }
      if (!formData.idCardBack) {
        newErrors.idCardBack = 'ID Card Back is required';
      }
    } else if (!formData.passport) {
      newErrors.passport = 'Passport is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    }

    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentChecked) {
      setErrors((prev) => ({
        ...prev,
        consent: 'Please accept the terms to continue',
      }));
      return;
    }

    setErrors({});

    try {
      const kycFormData = {
        ...formData,
        dateOfBirth: formData.dateOfBirth instanceof Date 
          ? formData.dateOfBirth.toISOString().split('T')[0]
          : formData.dateOfBirth,
        documentType,
      };

      await submitKYCMutation.mutateAsync(kycFormData);
      // Success is handled by the mutation hook
    } catch (error) {
      // Error is handled by the mutation hook, but we can set it in state too
      const errorMessage = error.response?.data?.error || error.message;
      setErrors((prev) => ({
        ...prev,
        submit: errorMessage,
      }));
    }
  };

  const selectCountry = (val) => {
    setFormData(prev => ({
      ...prev,
      country: val
    }));
    setErrors(prev => ({
      ...prev,
      country: ''
    }));
  };

  const renderStep1 = () => (
    <>
      <p className={styles.description}>
        For a more considerate service, please fill in the following information
        to let us know about you.
      </p>
      <form className={styles.form} onSubmit={handleNext}>
        <div className={styles.formGroup}>
          <label className={styles.label}>First Name</label>
          <input
            type="text"
            name="firstName"
            className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter first name"
          />
          {errors.firstName && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.firstName}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Last Name</label>
          <input
            type="text"
            name="lastName"
            className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter last name"
          />
          {errors.lastName && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.lastName}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Gender</label>
          <select
            name="gender"
            className={`${styles.select} ${errors.gender ? styles.inputError : ''}`}
            value={formData.gender}
            onChange={handleInputChange}
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {errors.gender && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.gender}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Date of Birth
            <Calendar className={styles.labelIcon} size={14} />
          </label>
          <div className={errors.dateOfBirth ? styles.datePickerError : ''}>
            <DatePicker
              selected={formData.dateOfBirth}
              onChange={handleDateChange}
              placeholderText="Select your date of birth"
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
            />
          </div>
          {errors.dateOfBirth && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.dateOfBirth}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <CountryDropdown
            value={formData.country}
            onChange={selectCountry}
            valueType="short"
            priorityOptions={['US', 'GB', 'CA', 'AU', 'IN']}
            className={`${styles.countrySelect} ${errors.country ? styles.inputError : ''}`}
          />
          {errors.country && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.country}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>City</label>
          <input
            type="text"
            name="city"
            className={`${styles.input} ${errors.city ? styles.inputError : ''}`}
            value={formData.city}
            onChange={handleInputChange}
            placeholder="Enter city"
          />
          {errors.city && (
            <span className={styles.errorText}>
              <AlertCircle size={14} />
              {errors.city}
            </span>
          )}
        </div>

        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.label}>Address</label>
          <input
            type="text"
            name="address"
            className={`${styles.input} ${errors.address ? styles.inputError : ''}`}
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Enter your address"
          />
          {errors.address && <span className={styles.errorText}>{errors.address}</span>}
        </div>

        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <button type="submit" className={styles.button}>
            Next
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </form>
    </>
  );

  const renderStep2 = () => (
    <>
      <h3 className={styles.stepTitle}>Choose Document Type</h3>

      <div className={styles.toggleContainer}>
        <button
          type="button"
          className={`${styles.toggleButton} ${documentType === 'idCard' ? styles.toggleButtonActive : ''}`}
          onClick={() => handleDocumentTypeChange('idCard')}
        >
          ID Card
        </button>
        <button
          type="button"
          className={`${styles.toggleButton} ${documentType === 'passport' ? styles.toggleButtonActive : ''}`}
          onClick={() => handleDocumentTypeChange('passport')}
        >
          Passport
        </button>
      </div>

      {documentType === 'idCard' ? (
        <>
          <p className={styles.uploadDescription}>
            Please upload a valid government-issued ID document or Driver's
            license. It must contain your full name, date of birth, your photo
            and the document's expiration date.
          </p>
          <div className={styles.uploadGrid}>
            <div className={styles.uploadContainer}>
              <div className={`${styles.uploadPreview} ${errors.idCardFront ? styles.uploadPreviewError : ''}`}>
                {previewImages.idCardFront ? (
                  <img src={previewImages.idCardFront} alt="ID Front Preview" />
                ) : (
                  <div className={styles.placeholderIcon}>ID Front</div>
                )}
              </div>
              <p className={styles.uploadLabel}>ID or Driver's license Front (.jpg, .png)</p>
              <p className={styles.uploadLabel}>File size limit 1MB</p>
              <input
                type="file"
                ref={fileInputRefs.idCardFront}
                accept="image/jpeg,image/png"
                onChange={(e) => handleFileChange(e, 'idCardFront')}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={styles.chooseFileButton}
                onClick={() => handleFileSelect('idCardFront')}
              >
                <Upload size={16} />
                Choose File
              </button>
              {errors.idCardFront && <span className={styles.errorText}>{errors.idCardFront}</span>}
            </div>

            <div className={styles.uploadContainer}>
              <div className={`${styles.uploadPreview} ${errors.idCardBack ? styles.uploadPreviewError : ''}`}>
                {previewImages.idCardBack ? (
                  <img src={previewImages.idCardBack} alt="ID Back Preview" />
                ) : (
                  <div className={styles.placeholderIcon}>ID Back</div>
                )}
              </div>
              <p className={styles.uploadLabel}>ID or Driver's license Back (.jpg, .png)</p>
              <p className={styles.uploadLabel}>File size limit 1MB</p>
              <input
                type="file"
                ref={fileInputRefs.idCardBack}
                accept="image/jpeg,image/png"
                onChange={(e) => handleFileChange(e, 'idCardBack')}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={styles.chooseFileButton}
                onClick={() => handleFileSelect('idCardBack')}
              >
                <Upload size={16} />
                Choose File
              </button>
              {errors.idCardBack && <span className={styles.errorText}>{errors.idCardBack}</span>}
            </div>
          </div>
        </>
      ) : (
        <>
          <p className={styles.uploadDescription}>
            Please upload the personal page of your passport, which includes
            your photo, name, date of birth, passport number and other personal
            information. Please make sure your passport is within the validity
            period.
          </p>
          <div className={styles.uploadGrid}>
            <div className={styles.uploadContainer}>
              <div className={`${styles.uploadPreview} ${errors.passport ? styles.uploadPreviewError : ''}`}>
                {previewImages.passport ? (
                  <img src={previewImages.passport} alt="Passport Preview" />
                ) : (
                  <div className={styles.placeholderIcon}>Passport</div>
                )}
              </div>
              <p className={styles.uploadLabel}>Passport (.jpg, .png)</p>
              <p className={styles.uploadLabel}>File size limit 1MB</p>
              <input
                type="file"
                ref={fileInputRefs.passport}
                accept="image/jpeg,image/png"
                onChange={(e) => handleFileChange(e, 'passport')}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={styles.chooseFileButton}
                onClick={() => handleFileSelect('passport')}
              >
                <Upload size={16} />
                Choose File
              </button>
              {errors.passport && <span className={styles.errorText}>{errors.passport}</span>}
            </div>
          </div>
        </>
      )}

      <div className={styles.buttonContainer}>
        <button type="button" className={`${styles.button} ${styles.buttonSecondary}`} onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
        <button type="button" className={styles.button} onClick={handleNext}>
          Next
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <h3 className={styles.stepTitle}>Confirm Your Information</h3>
      <p className={styles.description}>
        Please review your information before submitting
      </p>

      <div className={styles.reviewSection}>
        <h4 className={styles.reviewTitle}>Personal Information</h4>
        <div className={styles.reviewGrid}>
          <div className={styles.reviewItem}>
            <strong>Name:</strong> {formData.firstName} {formData.lastName}
          </div>
          <div className={styles.reviewItem}>
            <strong>Gender:</strong> {formData.gender}
          </div>
          <div className={styles.reviewItem}>
            <strong>Date of Birth:</strong> {formData.dateOfBirth instanceof Date 
              ? formData.dateOfBirth.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : formData.dateOfBirth}
          </div>
          <div className={styles.reviewItem}>
            <strong>Country:</strong> {formData.country}
          </div>
          <div className={styles.reviewItem}>
            <strong>City:</strong> {formData.city}
          </div>
          <div className={styles.reviewItem}>
            <strong>Address:</strong> {formData.address}
          </div>
        </div>

        <h4 className={styles.reviewTitle}>Uploaded Documents</h4>
        <div className={styles.documentPreviewGrid}>
          {documentType === 'idCard' ? (
            <>
              <div className={styles.documentPreview}>
                <p className={styles.documentName}>
                  ID Card Front: <span>{formData.idCardFront?.name}</span>
                </p>
                {previewImages.idCardFront && (
                  <img src={previewImages.idCardFront} alt="ID Front" />
                )}
              </div>
              <div className={styles.documentPreview}>
                <p className={styles.documentName}>
                  ID Card Back: <span>{formData.idCardBack?.name}</span>
                </p>
                {previewImages.idCardBack && (
                  <img src={previewImages.idCardBack} alt="ID Back" />
                )}
              </div>
            </>
          ) : (
            <div className={styles.documentPreview}>
              <p className={styles.documentName}>
                Passport: <span>{formData.passport?.name}</span>
              </p>
              {previewImages.passport && (
                <img src={previewImages.passport} alt="Passport" />
              )}
            </div>
          )}
        </div>

        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id="consent"
            checked={consentChecked}
            onChange={(e) => {
              setConsentChecked(e.target.checked);
              if (e.target.checked) {
                setErrors((prev) => ({ ...prev, consent: '' }));
              }
            }}
            className={styles.checkbox}
          />
          <label htmlFor="consent" className={styles.checkboxLabel}>
            I hereby declare that all the information provided is correct and
            accurate.
          </label>
        </div>
        {errors.consent && <span className={styles.errorText}>{errors.consent}</span>}
        {errors.submit && <span className={styles.errorText}>{errors.submit}</span>}

        <div className={styles.buttonContainer}>
          <button type="button" className={`${styles.button} ${styles.buttonSecondary}`} onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={handleSubmit}
            disabled={!consentChecked || submitKYCMutation.isPending}
          >
            {submitKYCMutation.isPending ? (
              <>Submitting...</>
            ) : (
              <>
                <CheckCircle2 size={18} />
                Submit
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );

  if (kycStatus === 'Pending') {
    return (
      <div className={styles.statusContainer}>
        <h3 className={styles.statusTitle}>KYC Verification Pending</h3>
        <p className={styles.statusMessage}>
          Your KYC submission is currently pending. Please wait for verification.
        </p>
      </div>
    );
  }

  if (kycStatus === 'Verified') {
    return (
      <div className={styles.statusContainer}>
        <h3 className={styles.statusTitle}>KYC Verified</h3>
        <p className={styles.statusMessage}>
          Your KYC has been verified. Thank you!
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Stepper */}
      <div className={styles.stepperContainer}>
        {[1, 2, 3].map((step) => (
          <React.Fragment key={step}>
            <div className={`${styles.stepCircle} ${step === currentStep ? styles.stepCircleActive : ''} ${step < currentStep ? styles.stepCircleCompleted : ''}`}>
              <span className={styles.stepText}>{step}</span>
            </div>
            {step < 3 && (
              <div className={`${styles.stepLine} ${step < currentStep ? styles.stepLineCompleted : ''}`}></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className={styles.contentContainer}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default VerificationForm;
